import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { SettingsNav } from "@/components/layout/settings-nav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { TemplateFormDialog } from "@/features/stage-templates/components/template-form-dialog";
import { useDeleteStageTemplate } from "@/features/stage-templates/hooks/use-delete-stage-template.ts";
import { useStageTemplates } from "@/features/stage-templates/hooks/use-stage-templates.ts";
import type { StageTemplate } from "@/features/stage-templates/types.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

type TemplateDialogState =
	| { mode: "create" }
	| { mode: "edit"; template: StageTemplate }
	| null;

export function StageTemplatesPage() {
	const { obra } = useParams();
	const navigate = useNavigate();
	const { has } = usePermissions();
	const canCreate = has("stage_templates:create");
	const canUpdate = has("stage_templates:update");
	const canDelete = has("stage_templates:delete");

	const templatesQuery = useStageTemplates();
	const deleteTemplate = useDeleteStageTemplate();
	const [dialog, setDialog] = useState<TemplateDialogState>(null);
	const [toDelete, setToDelete] = useState<StageTemplate | null>(null);

	const templates = templatesQuery.data ?? [];
	const detailPath = (id: string) =>
		`/${obra}/configuracoes/templates-de-etapas/${id}`;

	function handleConfirmDelete() {
		if (!toDelete) return;
		deleteTemplate.mutate(toDelete.id, {
			onSuccess: () => {
				toast.success("Template removido com sucesso!");
				setToDelete(null);
			},
			onError: () => {
				toast.error("Houve um erro ao remover o template.");
				setToDelete(null);
			},
		});
	}

	return (
		<div className="stage-templates-page">
			<SettingsNav />

			<div className="page-header">
				<div>
					<h1 className="page-title">Templates de Etapas</h1>
					<p className="page-subtitle">
						Modelos de etapas reutilizáveis em qualquer obra da conta.
					</p>
				</div>
				{canCreate && (
					<Button onClick={() => setDialog({ mode: "create" })}>
						<Plus />
						Novo template
					</Button>
				)}
			</div>

			{templatesQuery.isPending ? (
				<Card className="stage-templates-page__card">
					<div className="stage-templates-page__skeleton">
						{Array.from({ length: 3 }, (_, index) => (
							<Skeleton
								// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder
								key={index}
								className="stage-templates-page__skeleton-row"
							/>
						))}
					</div>
				</Card>
			) : templatesQuery.isError ? (
				<Card>
					<div className="card-state">
						<p>Não foi possível carregar os templates.</p>
						<Button variant="outline" onClick={() => templatesQuery.refetch()}>
							Tentar novamente
						</Button>
					</div>
				</Card>
			) : (
				<Card className="stage-templates-page__card">
					<Table>
						<TableHeader className="stage-templates-page__thead">
							<TableRow>
								<TableHead className="stage-templates-page__col-name">
									Nome
								</TableHead>
								<TableHead>Descrição</TableHead>
								<TableHead className="stage-templates-page__col-actions">
									Ações
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{templates.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={3}
										className="stage-templates-page__empty"
									>
										Nenhum template cadastrado.
									</TableCell>
								</TableRow>
							) : (
								templates.map((template) => (
									<TableRow key={template.id}>
										<TableCell className="stage-templates-page__name-cell">
											<Link
												to={detailPath(template.id)}
												className="stage-templates-page__name-link"
											>
												{template.name}
											</Link>
										</TableCell>
										<TableCell className="stage-templates-page__desc-cell">
											{template.description || "—"}
										</TableCell>
										<TableCell className="stage-templates-page__col-actions">
											<div className="stage-templates-page__row-actions">
												{canUpdate && (
													<Button
														variant="ghost"
														size="icon"
														className="stage-templates-page__icon-btn"
														onClick={() =>
															setDialog({ mode: "edit", template })
														}
														aria-label={`Editar ${template.name}`}
													>
														<Pencil />
													</Button>
												)}
												{canDelete && (
													<Button
														variant="ghost"
														size="icon"
														className="stage-templates-page__icon-btn--danger"
														onClick={() => setToDelete(template)}
														aria-label={`Remover ${template.name}`}
													>
														<Trash2 />
													</Button>
												)}
											</div>
											{(canUpdate || canDelete) && (
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="stage-templates-page__icon-btn--mobile"
															aria-label={`Abrir menu de ${template.name}`}
														>
															<MoreHorizontal />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={() => navigate(detailPath(template.id))}
														>
															Abrir itens
														</DropdownMenuItem>
														{canUpdate && (
															<DropdownMenuItem
																onClick={() =>
																	setDialog({ mode: "edit", template })
																}
															>
																<Pencil />
																Editar
															</DropdownMenuItem>
														)}
														{canDelete && <DropdownMenuSeparator />}
														{canDelete && (
															<DropdownMenuItem
																variant="destructive"
																onClick={() => setToDelete(template)}
															>
																<Trash2 />
																Remover
															</DropdownMenuItem>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											)}
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</Card>
			)}

			{dialog && (
				<TemplateFormDialog
					open
					onOpenChange={(open) => {
						if (!open) setDialog(null);
					}}
					mode={dialog.mode}
					template={dialog.mode === "edit" ? dialog.template : undefined}
					onCreated={(id) => navigate(detailPath(id))}
				/>
			)}

			<ConfirmDialog
				open={!!toDelete}
				onOpenChange={(open) => {
					if (!open) setToDelete(null);
				}}
				title="Remover template"
				description={
					toDelete
						? `Tem certeza que deseja remover o template “${toDelete.name}” e todos os seus itens? Esta ação não pode ser desfeita.`
						: ""
				}
				confirmLabel="Remover"
				destructive
				isPending={deleteTemplate.isPending}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	);
}
